import React from "react";
import { Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CALCULATOR } from "@/data/content";
import { Turnstile, TURNSTILE_SITE_KEY } from "@/components/site/Turnstile";

export const OrderForm = ({ form, setField, setConsent, errors, submitting, done, onSubmit, onReset, onToken, website, setWebsite }) => (
  <div className="mt-8 rounded-3xl border border-black/10 bg-white p-6 shadow-xl sm:p-8">
    <h2 className="flex items-center gap-3 font-display text-xl font-bold text-ink">
      <span className="h-5 w-1 rounded-full bg-brand-500" />
      Στείλτε αίτημα παραγγελίας
    </h2>
    <p className="mt-2 text-sm text-black/55">
      Συμπληρώστε τα στοιχεία σας και επικοινωνούμε το συντομότερο με επιβεβαιωμένη προσφορά.
    </p>

    {done ? (
      <div
        data-testid="order-success"
        className="mt-6 flex flex-col items-center rounded-2xl border border-brand-500/30 bg-brand-50/30 p-8 text-center"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand-500 text-white">
          <Check className="h-7 w-7" />
        </div>
        <h3 className="mt-4 font-display text-xl font-bold text-ink">Ευχαριστούμε!</h3>
        <p className="mt-2 max-w-md text-sm text-black/60">
          Λάβαμε το αίτημά σας. Η ομάδα μας θα επικοινωνήσει μαζί σας το συντομότερο.
        </p>
        <Button onClick={onReset} variant="outline" className="mt-6 rounded-xl border-black/15">
          Νέα παραγγελία
        </Button>
      </div>
    ) : (
      <form onSubmit={onSubmit} data-testid="calculator-order-request-form" noValidate className="mt-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-black/70">Όνομα *</label>
            <Input data-testid="order-first-name" value={form.first_name} onChange={setField("first_name")} className="mt-1.5" />
            {errors.first_name && <p className="mt-1 text-xs text-brand-500">{errors.first_name}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-black/70">Επώνυμο *</label>
            <Input data-testid="order-last-name" value={form.last_name} onChange={setField("last_name")} className="mt-1.5" />
            {errors.last_name && <p className="mt-1 text-xs text-brand-500">{errors.last_name}</p>}
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-black/70">Email *</label>
            <Input type="email" data-testid="order-email" value={form.email} onChange={setField("email")} className="mt-1.5" />
            {errors.email && <p className="mt-1 text-xs text-brand-500">{errors.email}</p>}
          </div>
          <div>
            <label className="text-sm font-medium text-black/70">Τηλέφωνο *</label>
            <Input data-testid="order-phone" value={form.phone} onChange={setField("phone")} className="mt-1.5" />
            {errors.phone && <p className="mt-1 text-xs text-brand-500">{errors.phone}</p>}
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-black/70">Σχόλια / Ειδικές απαιτήσεις</label>
          <Textarea data-testid="order-comments" value={form.comments} onChange={setField("comments")} rows={3} className="mt-1.5" />
        </div>
        <label className="flex cursor-pointer items-start gap-3 text-sm text-black/60">
          <Checkbox
            data-testid="order-consent-checkbox"
            checked={form.consent}
            onCheckedChange={setConsent}
            className="mt-0.5 data-[state=checked]:border-brand-500 data-[state=checked]:bg-brand-500"
          />
          <span>
            Αποδέχομαι τους{" "}
            <Link
              to="/oroi-epikoinonias"
              className="font-semibold text-brand-600 underline hover:text-brand-500"
            >
              όρους επικοινωνίας
            </Link>
            . *
          </span>
        </label>
        {errors.consent && <p className="-mt-2 text-xs text-brand-500">{errors.consent}</p>}

        {/* Honeypot — hidden from users. */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website || ""}
          onChange={(e) => setWebsite?.(e.target.value)}
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          aria-hidden="true"
        />

        {TURNSTILE_SITE_KEY && <Turnstile onVerify={onToken} />}

        <Button
          type="submit"
          data-testid="calculator-submit-order-request-button"
          disabled={submitting}
          className="h-12 w-full rounded-xl bg-brand-500 text-base font-semibold text-white shadow-lg hover:bg-brand-600"
        >
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Αποστολή αιτήματος παραγγελίας →</>}
        </Button>
        <p className="text-center text-xs text-black/45">{CALCULATOR.noPaymentNote}</p>
      </form>
    )}
  </div>
);
